/** Staff OAuth via search — phrases live only here; not in UI copy. */

const AUTH = new Set(["admin", "login", "админ", "логин"]);
const LOGOUT = new Set(["logout", "выход"]);

export function isAuthQuery(raw: string): boolean {
  return AUTH.has(String(raw || "").trim().toLowerCase());
}

export function isLogoutQuery(raw: string): boolean {
  return LOGOUT.has(String(raw || "").trim().toLowerCase());
}

export function isServiceKey(raw: string): boolean {
  return /^hk_[A-Za-z0-9_-]{40,80}$/.test(String(raw || "").trim());
}

export async function loginWithKey(key: string): Promise<void> {
  const res = await fetch("/api/auth/key", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ key: key.trim() }),
  });
  if (!res.ok) throw new Error("unauthorized");
}

function b64url(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function randomUrlSafe(n: number): string {
  const buf = new Uint8Array(n);
  crypto.getRandomValues(buf);
  return b64url(buf);
}

async function challengeS256(verifier: string): Promise<string> {
  const dig = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return b64url(dig);
}

const STATE_KEY = "hiera_oauth_state";
const VERIFIER_KEY = "hiera_oidc_verifier";
const RETURN_KEY = "hiera_oauth_return";
const INVITE_KEY = "hiera_invite";

export function captureInviteFromUrl(): { token: string | null; fromUrl: boolean } {
  const params = new URLSearchParams(window.location.search);
  const invite = String(params.get("invite") || "").trim();
  if (!invite) return { token: sessionStorage.getItem(INVITE_KEY), fromUrl: false };
  sessionStorage.setItem(INVITE_KEY, invite);
  const u = new URL(window.location.href);
  u.searchParams.delete("invite");
  const next = `${u.pathname}${u.search}${u.hash}`;
  window.history.replaceState({}, "", next || "/");
  return { token: invite, fromUrl: true };
}

export function peekInvite(): string {
  return String(sessionStorage.getItem(INVITE_KEY) || "").trim();
}

export function clearInvite(): void {
  sessionStorage.removeItem(INVITE_KEY);
}

export async function startLogin(returnTo = "/"): Promise<void> {
  const res = await fetch("/api/auth/config", { cache: "no-store", credentials: "same-origin" });
  if (!res.ok) throw new Error(`auth config ${res.status}`);
  const cfg = await res.json();
  const base = String(cfg.heronAuthUrl || "").replace(/\/+$/, "");
  const clientId = cfg.clientId || "hiera-web";
  if (!base) throw new Error("HERON auth URL not configured");

  const state = crypto.randomUUID();
  const verifier = randomUrlSafe(64);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(RETURN_KEY, returnTo);

  const redirectUri = `${window.location.origin}/auth/heron-callback/`;
  const challenge = await challengeS256(verifier);
  const q = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    scope: "openid profile email",
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  window.location.href = `${base}/oauth2/authorize?${q.toString()}`;
}

export async function finishCallback(): Promise<string> {
  const params = new URLSearchParams(window.location.search);
  if (params.get("error")) throw new Error(params.get("error") || "oauth_error");
  const code = (params.get("code") || "").trim();
  const state = params.get("state") || "";
  if (!code || state !== sessionStorage.getItem(STATE_KEY)) throw new Error("invalid_state");
  const verifier = sessionStorage.getItem(VERIFIER_KEY) || "";
  const invite = peekInvite();
  const redirectUri = `${window.location.origin}/auth/heron-callback/`;
  const res = await fetch("/api/auth/heron/exchange", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, codeVerifier: verifier, redirectUri, invite: invite || undefined }),
  });
  const payload = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) {
    const errCode = String(payload.error || "");
    if (errCode === "registration_closed") throw new Error("registration_closed");
    throw new Error(errCode || `exchange ${res.status}`);
  }
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  clearInvite();
  return sessionStorage.getItem(RETURN_KEY) || "/";
}

export async function logout(): Promise<void> {
  await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" });
}
